import clsx from "clsx";
import {
  Home,
  PanelLeftClose,
  PanelLeft,
  SquarePen,
  Search,
  RefreshCw,
  Clock,
  BarChart3,
  BookOpen,
  Shield,
  Binoculars,
} from "lucide-react";

import { useAutomations } from "@/hooks/useAutomations";
import { useSessions } from "@/hooks/useSessions";
import { useWatches } from "@/hooks/useWatches";
import { useStore } from "@/lib/store";

import { SidebarFilters } from "./SidebarFilters";
import { SidebarFooter } from "./SidebarFooter";
import { SidebarSessionList } from "./SidebarSessionList";

export function Sidebar(): JSX.Element {
  const filters = useStore((state) => state.filters);
  const setFilter = useStore((state) => state.setFilter);
  const theme = useStore((state) => state.theme);
  const sidebarOpen = useStore((state) => state.sidebarOpen);
  const sidebarCollapsed = useStore((state) => state.sidebarCollapsed);
  const closeSidebar = useStore((state) => state.closeSidebar);
  const toggleSidebarCollapsed = useStore((state) => state.toggleSidebarCollapsed);
  const activeView = useStore((state) => state.activeView);
  const setView = useStore((state) => state.setView);
  const user = useStore((state) => state.user);
  const isAdmin = user?.role === "admin";
  const { data, isLoading, refetch } = useSessions(filters);
  const { data: allData } = useSessions(
    { ...filters, mine: undefined },
    { enabled: !!filters.mine },
  );
  const allCount = filters.mine ? allData?.total : data?.total;
  const { data: automationsData } = useAutomations();
  const enabledCount =
    automationsData?.automations.filter((a) => a.status === "enabled").length ?? 0;
  const { data: watchesData } = useWatches();
  const activeWatchCount = watchesData?.total ?? 0;

  function setViewQueryParam(
    view: "watches" | "stats" | "docs" | "preferences" | "admin" | null,
  ): void {
    const url = new URL(window.location.href);
    url.searchParams.delete("thread");
    url.searchParams.delete("automation");
    url.searchParams.delete("doc");
    if (view) {
      url.searchParams.set("view", view);
    } else {
      url.searchParams.delete("view");
    }
    window.history.pushState({}, "", url);
  }

  function handleNewChat(): void {
    setView("sessions");
    setViewQueryParam(null);
    closeSidebar();
  }

  function handleHome(): void {
    setView("sessions");
    setViewQueryParam(null);
    closeSidebar();
  }

  function handleAutomations(): void {
    setView("automations");
    setViewQueryParam(null);
    closeSidebar();
  }

  function handleWatches(): void {
    setView("watches");
    setViewQueryParam("watches");
    closeSidebar();
  }

  function handleStats(): void {
    setView("stats");
    setViewQueryParam("stats");
    closeSidebar();
  }

  function handleDocs(): void {
    setView("docs");
    setViewQueryParam("docs");
    closeSidebar();
  }

  function handleAdmin(): void {
    setView("admin");
    setViewQueryParam("admin");
    closeSidebar();
  }

  return (
    <>
      {/* Mobile backdrop */}
      <div
        role="presentation"
        className={clsx("sidebar-backdrop md:hidden", sidebarOpen && "open")}
        onClick={closeSidebar}
      />

      {/* Sidebar */}
      <aside
        className={clsx(
          "sidebar overflow-hidden border-r border-cyber-border bg-cyber-bg",
          sidebarOpen && "open",
          sidebarCollapsed && "collapsed",
        )}
      >
        {/* Collapsed view - icon rail */}
        <div
          className={clsx(
            "absolute inset-0 flex flex-col items-center gap-3 py-3 transition-opacity duration-150",
            sidebarCollapsed ? "opacity-100" : "pointer-events-none opacity-0",
          )}
        >
          <button
            onClick={toggleSidebarCollapsed}
            className="hidden rounded p-2 text-cyber-muted transition-all hover:bg-cyber-surface hover:text-cyber-cyan md:flex"
            title="Expand sidebar"
          >
            <PanelLeft size={20} />
          </button>
          <button
            onClick={handleNewChat}
            className="rounded p-2 text-cyber-cyan transition-all hover:bg-cyber-cyan/10"
            title="New Chat"
          >
            <SquarePen size={20} />
          </button>
          <button
            onClick={toggleSidebarCollapsed}
            className="rounded p-2 text-cyber-muted transition-all hover:bg-cyber-surface hover:text-cyber-cyan"
            title="Search"
          >
            <Search size={20} />
          </button>
          <button
            onClick={() => refetch()}
            className="rounded p-2 text-cyber-muted transition-all hover:bg-cyber-surface hover:text-cyber-cyan"
            title="Refresh"
          >
            <RefreshCw size={20} />
          </button>
          <button
            onClick={handleAutomations}
            className={clsx(
              "rounded p-2 transition-all",
              activeView === "automations" || activeView === "automation-detail"
                ? "bg-cyber-cyan/10 text-cyber-cyan"
                : "text-cyber-muted hover:bg-cyber-surface hover:text-cyber-cyan",
            )}
            title="Automations"
          >
            <Clock size={20} />
          </button>
          <button
            onClick={handleStats}
            className={clsx(
              "rounded p-2 transition-all",
              activeView === "stats"
                ? "bg-cyber-cyan/10 text-cyber-cyan"
                : "text-cyber-muted hover:bg-cyber-surface hover:text-cyber-cyan",
            )}
            title="Stats"
          >
            <BarChart3 size={20} />
          </button>
          <button
            onClick={handleWatches}
            className={clsx(
              "rounded p-2 transition-all",
              activeView === "watches"
                ? "bg-cyber-cyan/10 text-cyber-cyan"
                : "text-cyber-muted hover:bg-cyber-surface hover:text-cyber-cyan",
            )}
            title="Watches"
          >
            <Binoculars size={20} />
          </button>
          <button
            onClick={handleDocs}
            className={clsx(
              "rounded p-2 transition-all",
              activeView === "docs"
                ? "bg-cyber-cyan/10 text-cyber-cyan"
                : "text-cyber-muted hover:bg-cyber-surface hover:text-cyber-cyan",
            )}
            title="Docs"
          >
            <BookOpen size={20} />
          </button>
          {isAdmin && (
            <button
              onClick={handleAdmin}
              className={clsx(
                "rounded p-2 transition-all",
                activeView === "admin"
                  ? "bg-cyber-cyan/10 text-cyber-cyan"
                  : "text-cyber-muted hover:bg-cyber-surface hover:text-cyber-cyan",
              )}
              title="Admin"
            >
              <Shield size={20} />
            </button>
          )}
          <div className="flex-1" />
          <SidebarFooter collapsed />
        </div>

        {/* Expanded view - full sidebar */}
        <div
          className={clsx(
            "flex h-full w-[280px] flex-col transition-opacity duration-150",
            sidebarCollapsed ? "pointer-events-none opacity-0" : "opacity-100",
          )}
        >
          {/* Header row: home + collapse */}
          <div className="flex items-center justify-between px-3 pb-2 pt-3">
            <button
              onClick={handleHome}
              className="rounded p-2 text-cyber-cyan transition-all hover:bg-cyber-cyan/10"
              title="Home"
              aria-label="Home"
            >
              <Home size={18} />
            </button>
            <div className="flex items-center gap-1">
              <button
                onClick={() => refetch()}
                className="rounded p-2 text-cyber-muted/60 transition-all hover:bg-cyber-surface/50 hover:text-cyber-muted"
                title="Refresh"
                aria-label="Refresh"
              >
                <RefreshCw size={14} />
              </button>
              <button
                onClick={toggleSidebarCollapsed}
                className="hidden rounded p-2 text-cyber-muted/60 transition-all hover:bg-cyber-surface/50 hover:text-cyber-muted md:flex"
                title="Collapse sidebar"
                aria-label="Collapse sidebar"
              >
                <PanelLeftClose size={14} />
              </button>
            </div>
          </div>

          {/* Primary actions: new chat + search */}
          <div className="space-y-2 border-b border-cyber-border px-3 pb-3">
            <button
              onClick={handleNewChat}
              className="flex h-8 w-full items-center justify-center gap-2 rounded border border-cyber-cyan/30 px-3 text-sm text-cyber-cyan transition-all hover:bg-cyber-cyan/10"
              title="New Chat"
            >
              <SquarePen size={14} />
              <span>New chat</span>
            </button>

            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search
                  size={18}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-cyber-muted"
                />
                <input
                  type="text"
                  value={filters.search || ""}
                  onChange={(e) => setFilter("search", e.target.value || undefined)}
                  placeholder="Search chats"
                  className={clsx(
                    "h-8 w-full rounded pl-10 pr-3 text-sm text-cyber-text placeholder-cyber-muted/70 transition-colors",
                    theme === "base"
                      ? "bg-cyber-border/40 hover:bg-cyber-border/50 focus:bg-cyber-border/50"
                      : "bg-cyber-surface hover:bg-cyber-surface focus:bg-cyber-surface",
                  )}
                />
              </div>
            </div>
          </div>

          {/* Filters */}
          <SidebarFilters allCount={allCount} />

          {/* Session list */}
          <SidebarSessionList sessions={data?.sessions ?? []} isLoading={isLoading} />

          {/* Views nav */}
          <div className="border-t border-cyber-border px-3 py-2">
            <button
              onClick={handleAutomations}
              className={clsx(
                "mb-1 flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm transition-all",
                activeView === "automations" || activeView === "automation-detail"
                  ? "bg-cyber-cyan/10 text-cyber-cyan"
                  : "text-cyber-muted hover:bg-cyber-surface/50 hover:text-cyber-text",
              )}
            >
              <Clock size={14} />
              <span>Automations</span>
              {enabledCount > 0 && (
                <span className="ml-auto rounded-full bg-cyber-surface px-1.5 py-0.5 text-[10px] text-cyber-muted">
                  {enabledCount}
                </span>
              )}
            </button>
            <button
              onClick={handleStats}
              className={clsx(
                "mb-1 flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm transition-all",
                activeView === "stats"
                  ? "bg-cyber-cyan/10 text-cyber-cyan"
                  : "text-cyber-muted hover:bg-cyber-surface/50 hover:text-cyber-text",
              )}
            >
              <BarChart3 size={14} />
              <span>Stats</span>
            </button>
            <button
              onClick={handleWatches}
              className={clsx(
                "mb-1 flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm transition-all",
                activeView === "watches"
                  ? "bg-cyber-cyan/10 text-cyber-cyan"
                  : "text-cyber-muted hover:bg-cyber-surface/50 hover:text-cyber-text",
              )}
            >
              <Binoculars size={14} />
              <span>Watches</span>
              {activeWatchCount > 0 && (
                <span className="ml-auto rounded-full bg-cyber-surface px-1.5 py-0.5 text-[10px] text-cyber-muted">
                  {activeWatchCount}
                </span>
              )}
            </button>
            <button
              onClick={handleDocs}
              className={clsx(
                "mb-1 flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm transition-all",
                activeView === "docs"
                  ? "bg-cyber-cyan/10 text-cyber-cyan"
                  : "text-cyber-muted hover:bg-cyber-surface/50 hover:text-cyber-text",
              )}
            >
              <BookOpen size={14} />
              <span>Docs</span>
            </button>
            {isAdmin && (
              <button
                onClick={handleAdmin}
                className={clsx(
                  "mb-1 flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm transition-all",
                  activeView === "admin"
                    ? "bg-cyber-cyan/10 text-cyber-cyan"
                    : "text-cyber-muted hover:bg-cyber-surface/50 hover:text-cyber-text",
                )}
              >
                <Shield size={14} />
                <span>Admin</span>
              </button>
            )}
          </div>

          {/* User footer */}
          <SidebarFooter />
        </div>
      </aside>
    </>
  );
}
